// ─────────────────────────────────────────────────────────────────────────────
// audio_effects_rack.ts — Shared post-mixer effects
//
// One rack downstream of the mixer holds every environmental effect the
// scene can dial in. All effects ship bypassed (signal passes through
// unmodified) and only audibly engage once explicitly activated.
//
//   cutoff  (occlusion) — serial low-pass; bypass = high cutoff freq so
//                          the filter is transparent. Activating drops
//                          the cutoff for muffled / behind-wall feel.
//   reverb  (echo / room) — parallel send into a synthetic ConvolverNode;
//                            bypass = wet send at 0. The IR is built
//                            lazily on first activation.
//   delay   (texture / granulation) — parallel send into a feedback
//                                      delay line; bypass = wet at 0,
//                                      feedback at 0.
//
// The rack is mono in / mono out (channelCount=1 enforced upstream by
// the engine). New effect kinds can be added incrementally; today's
// three cover the "denote-environment" use case the journal needs.
// ─────────────────────────────────────────────────────────────────────────────

const CUTOFF_BYPASS_HZ = 22000;   // far above audible — transparent
const CUTOFF_DEFAULT_HZ = 800;    // rule-of-thumb "muffled through wall"

const REVERB_DEFAULT_DECAY_S = 1.6;
const REVERB_DEFAULT_LEVEL = 0.4;

const DELAY_DEFAULT_TIME_S = 0.25;
const DELAY_DEFAULT_FEEDBACK = 0.3;
const DELAY_DEFAULT_LEVEL = 0.3;

export class EffectsRack {
  /** Mixer connects here. */
  readonly input: GainNode;
  /** Master pulls from here. */
  readonly output: GainNode;

  // Cutoff (always in serial path; freq controls bypass)
  private readonly _cutoff: BiquadFilterNode;

  // Reverb (parallel send)
  private readonly _reverbSend: GainNode;
  private _reverbConvolver: ConvolverNode | null = null;

  // Delay (parallel send + feedback loop)
  private readonly _delayNode: DelayNode;
  private readonly _delaySend: GainNode;
  private readonly _delayFeedback: GainNode;

  constructor(private readonly _ctx: AudioContext) {
    this.input = _ctx.createGain();
    this.output = _ctx.createGain();

    // ── Serial: input → cutoff → output ──────────────────────────────
    this._cutoff = _ctx.createBiquadFilter();
    this._cutoff.type = 'lowpass';
    this._cutoff.frequency.value = CUTOFF_BYPASS_HZ;
    this.input.connect(this._cutoff).connect(this.output);

    // ── Parallel reverb send (convolver lazy) ────────────────────────
    this._reverbSend = _ctx.createGain();
    this._reverbSend.gain.value = 0;
    this._cutoff.connect(this._reverbSend);
    // _reverbConvolver wired into output on first activate

    // ── Parallel delay send ──────────────────────────────────────────
    this._delayNode = _ctx.createDelay(2.0);  // 2 s max
    this._delayNode.delayTime.value = DELAY_DEFAULT_TIME_S;
    this._delayFeedback = _ctx.createGain();
    this._delayFeedback.gain.value = 0;
    this._delaySend = _ctx.createGain();
    this._delaySend.gain.value = 0;
    // Routing: cutoff → delayNode → (feedback loop, send to output)
    this._cutoff.connect(this._delayNode);
    this._delayNode.connect(this._delayFeedback).connect(this._delayNode);
    this._delayNode.connect(this._delaySend).connect(this.output);
  }

  // ── Cutoff (occlusion) ────────────────────────────────────────────────

  setCutoff(active: boolean, freqHz: number = CUTOFF_DEFAULT_HZ): void {
    const target = active ? freqHz : CUTOFF_BYPASS_HZ;
    this._cutoff.frequency.setTargetAtTime(target, this._ctx.currentTime, 0.05);
  }

  // ── Reverb (echo / amplitude / room) ──────────────────────────────────

  /** Activate or bypass the reverb. `level` is the wet send (0..1).
   *  `decayS` triggers a fresh IR if non-default. */
  setReverb(
    active: boolean,
    level: number = REVERB_DEFAULT_LEVEL,
    decayS: number = REVERB_DEFAULT_DECAY_S,
  ): void {
    if (active && !this._reverbConvolver) {
      this._reverbConvolver = this._ctx.createConvolver();
      this._reverbConvolver.buffer = synthesizeIR(this._ctx, decayS);
      this._reverbSend.connect(this._reverbConvolver).connect(this.output);
    } else if (active && this._reverbConvolver && decayS !== REVERB_DEFAULT_DECAY_S) {
      this._reverbConvolver.buffer = synthesizeIR(this._ctx, decayS);
    }
    const target = active ? level : 0;
    this._reverbSend.gain.setTargetAtTime(target, this._ctx.currentTime, 0.05);
  }

  // ── Delay (texture / granulation) ─────────────────────────────────────

  setDelay(
    active: boolean,
    timeS: number = DELAY_DEFAULT_TIME_S,
    feedback: number = DELAY_DEFAULT_FEEDBACK,
    level: number = DELAY_DEFAULT_LEVEL,
  ): void {
    const now = this._ctx.currentTime;
    this._delayNode.delayTime.setTargetAtTime(timeS, now, 0.05);
    this._delayFeedback.gain.setTargetAtTime(active ? feedback : 0, now, 0.05);
    this._delaySend.gain.setTargetAtTime(active ? level : 0, now, 0.05);
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Synthesize a small-room impulse response: white noise with exponential
 *  decay, single channel. Cheap, doesn't need an external sample. */
function synthesizeIR(ctx: AudioContext, decayS: number): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.max(1, Math.floor(sampleRate * decayS));
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    const t = i / length;
    const env = Math.pow(1 - t, 3);  // cubic decay
    data[i] = (Math.random() * 2 - 1) * env;
  }
  return buffer;
}
